import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import MsaLogo from './MsaLogo';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Planos', to: '/planos' },
  { label: 'Contato', to: '/contatos' },
  { label: 'FAQ', to: '/', isFaq: true },
];

const CENTRAL_ASSINANTE_URL = 'https://ixc.faynetrj.com.br/central_assinante_web/login';

const quickLinks = [
  {
    label: '2ª Via',
    to: '/2via',
    title: '2ª Via da Fatura',
    variant: 'violet',
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 2h16v20l-3-2-2 2-2-2-2 2-2-2-2 2-3-2z" />
        <line x1="8" y1="7" x2="16" y2="7" />
        <line x1="8" y1="11" x2="16" y2="11" />
        <line x1="8" y1="15" x2="13" y2="15" />
      </svg>
    ),
  },
  {
    label: 'Solicitações',
    to: '/solicitacoes',
    title: 'Área de Solicitações',
    variant: 'blue',
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12h6M9 16h6M9 8h1" />
        <path d="M8 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
      </svg>
    ),
  },
];

const subscriberLink = {
  label: 'Central do Assinante',
  href: CENTRAL_ASSINANTE_URL,
  title: 'Central do Assinante',
  variant: 'outline',
  icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [promoVisible, setPromoVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  const closeMenu = () => setMenuOpen(false);

  // Rola até a seção FAQ da Home; se estiver em outra página, navega antes de rolar
  const goToFaq = (event) => {
    event.preventDefault();
    closeMenu();

    const scrollToFaqSection = () => {
      document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (location.pathname === '/') {
      scrollToFaqSection();
    } else {
      navigate('/', { state: { scrollTo: 'faq' } });
    }
  };

  // Sombra de elevação ao rolar a página
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fecha o menu ao trocar de rota
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const applyOffset = () => {
      if (mediaQuery.matches) {
        root.style.setProperty('--mobile-navbar-offset', promoVisible ? '152px' : '84px');
        return;
      }

      root.style.setProperty('--mobile-navbar-offset', '0px');
    };

    applyOffset();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', applyOffset);
    } else {
      mediaQuery.addListener(applyOffset);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', applyOffset);
      } else {
        mediaQuery.removeListener(applyOffset);
      }
      root.style.removeProperty('--mobile-navbar-offset');
    };
  }, [promoVisible]);

  // Fecha com Esc, clique fora e trava o scroll do body enquanto o menu mobile está aberto
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const onClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const desktopActionLinks = (
    <>
      <div className="navbar__quick-links">
        {quickLinks.map((link) => (
          <Link
            key={link.label}
            to={link.to}
            className="navbar__quick-link"
            onClick={closeMenu}
            title={link.title}
          >
            <span className="navbar__quick-link-icon">{link.icon}</span>
            <span className="navbar__quick-link-label">{link.label}</span>
          </Link>
        ))}
      </div>

      <a
        href={subscriberLink.href}
        className="navbar__avatar"
        target="_blank"
        rel="noopener noreferrer"
        title={subscriberLink.title}
        onClick={closeMenu}
      >
        <span className="navbar__avatar-icon">{subscriberLink.icon}</span>
        <span className="navbar__avatar-label">{subscriberLink.label}</span>
      </a>
    </>
  );

  const mobileSideLinks = (
    <div className="navbar__mobile-side-actions">
      {quickLinks.filter((link) => link.label !== '2ª Via').map((link) => (
        <Link
          key={link.label}
          to={link.to}
          className={`navbar__mobile-side-link navbar__mobile-side-link--${link.variant}`}
          title={link.title}
          onClick={closeMenu}
        >
          <span className="navbar__mobile-side-link-icon">{link.icon}</span>
          <span className="navbar__mobile-side-link-label">{link.label}</span>
        </Link>
      ))}

      <a
        href={subscriberLink.href}
        className={`navbar__mobile-side-link navbar__mobile-side-link--${subscriberLink.variant}`}
        target="_blank"
        rel="noopener noreferrer"
        title={subscriberLink.title}
        onClick={closeMenu}
      >
        <span className="navbar__mobile-side-link-icon">{subscriberLink.icon}</span>
        <span className="navbar__mobile-side-link-label navbar__mobile-side-link-label--stacked">
          <span>Central do</span>
          <span>Assinante</span>
        </span>
      </a>
    </div>
  );

  return (
    <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${promoVisible ? 'navbar--promo-visible' : 'navbar--promo-hidden'}`} ref={navRef}>
      <div className={`navbar__promo ${promoVisible ? '' : 'navbar__promo--hidden'}`} aria-hidden={!promoVisible}>
        <button
          type="button"
          className="navbar__promo-close"
          aria-label="Fechar aviso de 2ª via"
          onClick={() => setPromoVisible(false)}
        >
          <span />
          <span />
        </button>

        <Link to="/2via" className="navbar__promo-content" onClick={closeMenu}>
          <span className="navbar__promo-icon">
            {quickLinks[0].icon}
          </span>
          <span className="navbar__promo-copy">
            <strong className="navbar__promo-title">2ª Via de Fatura</strong>
            <span className="navbar__promo-text">Acesse agora e emita a 2ª via da sua fatura de forma rápida e segura.</span>
          </span>
        </Link>

        <Link to="/2via" className="navbar__promo-cta" onClick={closeMenu}>
          Acessar
        </Link>
      </div>

      <div className="navbar__main">
        <div className="navbar__container">
          <div className="navbar__mobile-toggle-slot">
            <button
              className={`navbar__hamburger ${menuOpen ? 'navbar__hamburger--open' : ''}`}
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              aria-controls="navbar-menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>

          <Link to="/" className="navbar__logo" onClick={closeMenu}>
            <MsaLogo />
          </Link>

          <nav
            id="navbar-menu"
            className={`navbar__nav ${menuOpen ? 'navbar__nav--open' : ''}`}
          >
            <ul className="navbar__list">
              {navLinks.map((link) => (
                <li key={link.label} className="navbar__item">
                  <Link
                    to={link.to}
                    className={`navbar__link ${!link.isFaq && location.pathname === link.to ? 'navbar__link--active' : ''}`}
                    onClick={link.isFaq ? goToFaq : closeMenu}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="navbar__nav-actions">
              {desktopActionLinks}
            </div>
          </nav>

          <div className="navbar__actions">
            <div className="navbar__actions-desktop">
              {desktopActionLinks}
            </div>
          </div>

          <div className="navbar__mobile-balance" aria-hidden="true">
            {mobileSideLinks}
          </div>
        </div>
      </div>
      <div className="navbar__accent-line" />
    </header>
  );
}

export default Navbar;
